import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import {
	AccountInfo,
	AuthenticationResult,
	InteractionRequiredAuthError
} from '@azure/msal-browser';
import {
	BehaviorSubject,
	catchError,
	EMPTY,
	map,
	Observable,
	of,
	switchMap,
	tap,
	throwError
} from 'rxjs';

import { environment } from '../../environments/environment';

import { AUTO_LOGIN_ATTEMPTED_KEY, EXPLICIT_SIGNOUT_KEY } from './auth.keys';

const LOGIN_SCOPES = [
	'openid',
	'profile',
	'api://30d25383-29be-4691-9c42-d9ff2a746e83/API.Read',
	'user.read'
];
const API_SCOPE = 'api://30d25383-29be-4691-9c42-d9ff2a746e83/API.Read';

/**
 * 'initializing' is a distinct third state, not a synonym for 'unauthenticated'. Until
 * init() settles we do not know whether there is a session, and rendering a SIGN IN
 * button in that window reads as a spurious logout.
 */
export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated' | 'failed';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private _authSubject = new BehaviorSubject<AuthenticationResult | null>(null);
	user$ = this._authSubject.asObservable();

	private _statusSubject = new BehaviorSubject<AuthStatus>('initializing');
	status$ = this._statusSubject.asObservable();

	/** Synchronous mirror of status$. Guards branch on this; §3 rejects making them async. */
	readonly status = signal<AuthStatus>('initializing');

	/** 'failed' latches: a late settle must not clear the card while the rejected navigation stands. */
	markFailed(): void {
		if (this.status() !== 'failed') {
			this._setStatus('failed');
		}
	}

	private _setStatus(next: AuthStatus): void {
		if (this.status() === 'failed' && next !== 'failed') {
			return;
		}
		this.status.set(next);
		this._statusSubject.next(next);
	}

	/**
	 * Falls back to the stored AccountInfo, which is not an AuthenticationResult but shares
	 * the `idTokenClaims` shape that RoleService reads. Callers must not touch accessToken.
	 */
	getCurrentUser(): AuthenticationResult | null {
		return (
			this._authSubject.value ?? (this.loadAccountFromLocal() as AuthenticationResult | null)
		);
	}

	hasStoredAccount(): boolean {
		return !!this.loadAccountFromLocal();
	}

	constructor(
		private msalService: MsalService,
		private router: Router
	) {}

	getAccessToken(): Observable<string> {
		const account = this.loadAccountFromLocal();
		if (!account) {
			return throwError(() => new Error('No authenticated account available'));
		}
		const requestObj = { scopes: [API_SCOPE], account };
		return this.msalService.acquireTokenSilent(requestObj).pipe(
			catchError(error => {
				if (error instanceof InteractionRequiredAuthError) {
					if (environment.auth.method === 'redirect') {
						return this.msalService
							.acquireTokenRedirect(requestObj)
							.pipe(switchMap(() => EMPTY));
					}
					return this.msalService.acquireTokenPopup(requestObj);
				}
				return throwError(() => error);
			}),
			tap(result => {
				this.storeAccount(result.account);
				this._authSubject.next(result);
				this._setStatus('authenticated');
			}),
			map(result => result.accessToken)
		);
	}

	init(): Observable<AuthenticationResult | null> {
		return this.msalService.initialize().pipe(
			switchMap(() => {
				return this.msalService.handleRedirectObservable().pipe(
					catchError(err => {
						const errorMessage = err?.message || err?.toString() || '';
						const errorCode = err?.errorCode || err?.code || '';

						if (
							errorCode === 'hash_empty_error' ||
							errorMessage.includes('hash_empty_error') ||
							errorMessage.includes(
								'Hash value cannot be processed because it is empty'
							)
						) {
							return of(null);
						}
						console.error('Unexpected error in handleRedirectObservable:', err);
						return throwError(() => err);
					})
				);
			}),
			switchMap(redirectResult => {
				// Redirect login flow — use the result directly, no need for silent token
				if (redirectResult) {
					return of(redirectResult);
				}

				const account = this.loadAccountFromLocal();
				if (!account) {
					return of(null);
				}
				const requestObj = {
					scopes: [API_SCOPE],
					account
				};
				return this.msalService.acquireTokenSilent(requestObj).pipe(
					catchError(error => {
						if (error instanceof InteractionRequiredAuthError) {
							console.log(
								'Silent token failed, falling back to interactive login...'
							);

							if (environment.auth.method === 'redirect') {
								return this.msalService
									.acquireTokenRedirect(requestObj)
									.pipe(switchMap(() => EMPTY));
							}

							return this.msalService.acquireTokenPopup(requestObj).pipe(
								catchError(popupErr => {
									if (
										popupErr?.errorCode === 'hash_empty_error' ||
										popupErr?.message?.includes('hash_empty_error')
									) {
										console.warn(
											'Hash empty error during token popup - retrying...'
										);
										return this.msalService.acquireTokenPopup(requestObj);
									}
									return throwError(() => popupErr);
								})
							);
						}

						return throwError(() => error);
					})
				);
			}),
			map(t => {
				if (!!t) {
					this.storeAccount(t.account);
					this._authSubject.next(t);
					this._setStatus('authenticated');

					return t;
				}
				this._authSubject.next(t);
				this._setStatus('unauthenticated');
				return null;
			}),
			catchError(err => {
				const errorMessage = err?.message || err?.toString() || '';
				const errorCode = err?.errorCode || err?.code || '';

				if (
					errorCode === 'hash_empty_error' ||
					errorMessage.includes('hash_empty_error') ||
					errorMessage.includes('Hash value cannot be processed because it is empty')
				) {
					console.log('Hash empty error caught (expected in Azure SWA), continuing...');
					this.settleStatus();
					return of(null);
				}

				console.error('Error in auth resolver:', err);
				this.settleStatus();
				return of(null);
			})
		);
	}

	login(): Observable<AuthenticationResult | void> {
		if (environment.auth.method === 'redirect') {
			return this.msalService.loginRedirect({ scopes: LOGIN_SCOPES });
		}

		return this.msalService.loginPopup({ scopes: LOGIN_SCOPES }).pipe(
			tap(r => this.applyLoginResult(r)),
			catchError(err => {
				if (
					err?.errorCode === 'hash_empty_error' ||
					err?.message?.includes('hash_empty_error')
				) {
					console.warn(
						'Hash empty error during popup login - this may be due to Azure Static Web Apps routing. Retrying...'
					);
					return this.msalService
						.loginPopup({ scopes: LOGIN_SCOPES })
						.pipe(tap(r => this.applyLoginResult(r)));
				}
				return throwError(() => err);
			})
		);
	}

	private applyLoginResult(result: AuthenticationResult | null): void {
		if (!!result) {
			this.storeAccount(result.account);
		}
		this._authSubject.next(result);
		this._setStatus(!!result ? 'authenticated' : 'unauthenticated');
	}

	logout(): Observable<void> {
		sessionStorage.setItem(EXPLICIT_SIGNOUT_KEY, '1');
		if (environment.auth.method === 'redirect') {
			this.clearAccount();
			return this.msalService.logoutRedirect();
		}

		return this.msalService.logoutPopup().pipe(
			tap(() => this.clearAccount()),
			switchMap(() => {
				return this.router.navigateByUrl('/');
			}),
			map(() => {})
		);
	}

	/**
	 * Tolerates the literal strings 'null'/'undefined' and malformed JSON. Both were
	 * writable by the old unconditional setItem and made every downstream read either
	 * throw synchronously or read as authenticated on a truthiness check.
	 */
	private loadAccountFromLocal(): AccountInfo | null {
		const raw = localStorage.getItem('account');
		if (!raw) {
			return null;
		}
		try {
			const parsed = JSON.parse(raw) as AccountInfo | null;
			return parsed?.homeAccountId ? parsed : null;
		} catch {
			return null;
		}
	}

	private storeAccount(account: AccountInfo | null): void {
		if (!account) {
			return;
		}
		sessionStorage.removeItem(EXPLICIT_SIGNOUT_KEY);
		sessionStorage.removeItem(AUTO_LOGIN_ATTEMPTED_KEY);
		localStorage.setItem('account', JSON.stringify(account));
	}

	/**
	 * Leaving status at 'initializing' after a failed init would hide the SIGN IN button
	 * forever. Deliberately NOT called on the acquireTokenRedirect path, where the chain
	 * completes empty because the browser is already navigating to the login page.
	 */
	private settleStatus(): void {
		if (this._statusSubject.value === 'initializing') {
			this._setStatus('unauthenticated');
		}
	}

	private clearAccount(): void {
		localStorage.removeItem('account');
		this._authSubject.next(null);
		this._setStatus('unauthenticated');
	}
}
